import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AdminMaster from '../../../Layout/admin/Master';
import { EXCLUDED_PAYER_LIST } from '../../../routeNames';
import { changeAllTitleFunction, getRedirectTabId } from '../Dashboard/functions';
import ClientDropDown from '../../common/PartnerClientFacilityDropDown/ClientDropDown';
import FacilityDropDown from '../../common/PartnerClientFacilityDropDown/FacilityDropDown';
import AsyncSelect from 'react-select/async';
import { createOrUpdateExcluded, getSingleExcludedDetail } from '../../../Redux/Excludedpayer/action';
import { getPayerDropdownForLazySearch } from '../../../Redux/client/action';


const validationSchema = Yup.object().shape({
    clients: Yup.array()
        .of(Yup.mixed().test('is-string-or-number', 'Each client must be a string or number', value => typeof value === 'string' || typeof value === 'number'))
        .min(1, 'Required')
        .required('Required'),
    facility: Yup.array()
        .of(Yup.mixed().test('is-string-or-number', 'Each facility must be a string or number', value => typeof value === 'string' || typeof value === 'number'))
        .min(1, 'Required')
        .required('Required'),
    payer: Yup.array()
        .of(Yup.object().shape({
            value: Yup.string().required(),
            label: Yup.string().required()
        }))
        .min(1, 'Required')
        .required('Required'),
});

// Custom dropdown wrappers
const ControlledClientDropDown = ({ watch, trigger, control, name, ...props }) => (
    <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
            <ClientDropDown
                lockboxformik={{
                    
                    values: { clients: (watch('clients') || []).map(Number) },
                    setFieldValue: (fieldName, value) => {
                        field.onChange(value);
                        trigger(fieldName);
                    },
                    errors: error ? { clients: error?.message } : {},
                    touched: { clients: true }
                }}
                
                isSingle={true}
                isDisabled={true}
                width='100%'
                isPartner={false}
                {...props}
            />
        )}
    />
);

const ControlledFacilityDropDown = ({ trigger, watch, control, name, ...props }) => (
    <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
            <FacilityDropDown
                lockboxformik={{
                    values: { facility: (field.value || [])?.map(Number), clients: (watch("clients") || [])?.map(Number) },
                    setFieldValue: (fieldName, value) => {
                        // ✅ Only update the value, no trigger!
                        field.onChange(value);
                    },
                    errors: error ? { facility: error.message } : {},
                    touched: { facility: true }
                }}
                isSingle={true}
                isDisabled={true}
                width='100%'
                {...props}
            />
        )}
    />
);



const ControlledPayerDropDown = ({ trigger, watch, control, loadOptions, name, ...props }) => (
    <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
            <div>
                <AsyncSelect
                    isMulti
                    placeholder="Select an Option"
                    classNamePrefix="select form-control-solid form-control"
                    loadOptions={loadOptions}
                    isClearable={true}
                    value={field.value}
                    onChange={(selected) => {
                        field.onChange(selected);
                        trigger(name);
                    }}
                    onBlur={field.onBlur}
                    name={field.name}
                    inputRef={field.ref}
                    styles={{
                        control: (base, state) => ({
                            ...base,
                            backgroundColor: "hsl(204deg 33.33% 97.06%)",
                            border: 'none',
                            boxShadow: 'none',
                            '&:hover': {
                                border: 'none',
                            },
                        }),
                        placeholder: (base) => ({
                            ...base,
                            textTransform: 'uppercase',
                        }),
                        indicatorsContainer: (base) => ({
                            ...base,
                            border: 'none',
                        }),
                    }}
                    {...props}
                />

                {error && <p className="text-danger mt-1">{error.message}</p>}
            </div>
        )}
    />
);

const Createexcludedpayer = () => {
    const { id } = useParams();

    const [payeroptionns, SetpayerIdOptions] = useState();
    const [state, setState] = useState({
        loading: false,
        error: null,
        removedIds: [],
        toast: {
            show: false,
            type: '',
            message: ''
        }
    });


const defaultValues = useMemo(() => ({
  excluded_payer:Number?.(id),
  clients: [],
  facility: [],
  payer: []
}), [id]);


    const {
        trigger,
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isSubmitting, isValid }
    } = useForm({
        defaultValues,
        resolver: yupResolver(validationSchema),
        mode: 'onSubmit' 
    });

    const showToast = useCallback((type, message, duration = 3000) => {
        setState(prev => ({
            ...prev,
            toast: { show: true, type, message }
        }));

        setTimeout(() => {
            setState(prev => ({
                ...prev,
                toast: { show: false, type: '', message: '' }
            }));
        }, duration);
    }, []);

    const onSubmit = async (data) => {

        try {
            setState(prev => ({ ...prev, loading: true }));
            const redirectId = getRedirectTabId();
            const { excluded_payer, clients, facility, payer, ...rest } = data;
            const blocked_Payer = payer?.map(p => Number(p.value));
            const payload = {

                ...(id ? { excluded_payer } : {}),
                ...rest,
                blocked_by: clients[0],
                blocked_facility: facility[0],
                blocked_Payer: blocked_Payer,
            };

            const response = await new Promise((resolve) => {
                createOrUpdateExcluded(payload)(resolve);
            });

            if (response?.status && response?.status_code === 201) {
                showToast('success', id ? 'Updated successfully!' : 'Created successfully!', 1000);
                setTimeout(() => {
                    changeAllTitleFunction("Excluded Payer", EXCLUDED_PAYER_LIST, redirectId);
                }, 1000);
            } else {

                const errorMessage = response?.response?.data?.errors?.excluded_payer?.[0] || 'Something went wrong';
                showToast('error', errorMessage, 2000);
            }
        } catch (error) {
            showToast('error', 'An unexpected error occurred', 2000);
        } finally {
            setState(prev => ({ ...prev, loading: false }));
        }
    };

    let debounceTimeout;

    const loadOptions = useCallback((inputValue) => {
        return new Promise((resolve) => {
            clearTimeout(debounceTimeout);

            if (inputValue.length <= 2) {
                return resolve([]);
            }

            debounceTimeout = setTimeout(() => {
                const client_id = watch("clients");

                if (!client_id || client_id.length === 0) {
                    return resolve([]);
                }

                getPayerDropdownForLazySearch({ payer_name: inputValue, client_id })(response => {
                    if (response?.status && response?.data?.results) {
                        resolve(response.data.results);
                    } else {
                        resolve([]);
                    }
                });
            }, 500);
        });
    }, [watch]);

    useEffect(() => {
        if (id) {
            const loadData = async () => {
                try {
                    setState(prev => ({ ...prev, loading: true }));
                    const response = await new Promise((resolve) => {
                        getSingleExcludedDetail(id)(resolve);

                    });

                    if (response?.status && response?.data) {

                        const data = response.data?.data;
                        
                        setValue('clients', [data.blocked_by]);
                        setValue('facility', [data.blocked_facility]);
                        setValue('payer', response.data.data?.blocked_Payer||[]);
                    }

                } catch (error) {
                    console.log(error);
                    
                   
                } finally {
                    setState(prev => ({ ...prev, loading: false }));
                }
            };
            loadData();
        }
    }, [id, setValue]);

    const handleCancel = useCallback(() => {
        changeAllTitleFunction("Excluded Payer", EXCLUDED_PAYER_LIST);
    }, []);

    return (
        <React.Fragment>
            <AdminMaster
                ToastMessage={state.toast.show ? state.toast.type : false}
                message={state.toast.message}
            />

            <main id="main" className="main">
                <div className="pagetitle tab-fixed-header">
                    <h1>{id ? 'Edit Excluded Payer' : 'Create Excluded Payer'}</h1>
                </div>

                <section className="section mt-5">
                    <div className="card card-flush py-4">
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <div className='px-4 py-2'>
                                <div className="d-flex flex-wrap gap-5 mb-4">
                                    <div className='col-lg-4'>
                                        <Form.Label className='fw-bold'>Client</Form.Label>
                                        <ControlledClientDropDown
                                            control={control}
                                            name="clients"
                                            trigger={trigger}
                                            watch={watch}
                                        />
                                    </div>
                                    <div className='col-lg-4'>
                                        <Form.Label className='fw-bold'>Facility</Form.Label>
                                        <ControlledFacilityDropDown
                                            control={control}
                                            name="facility"
                                            watch={watch}
                                            trigger={trigger}
                                        />
                                    </div>
                                    <div className='col-lg-4'>
                                        <Form.Label className='fw-bold'>Payer</Form.Label>
                                        <ControlledPayerDropDown
                                            control={control}
                                            name="payer"
                                            trigger={trigger}
                                            watch={watch}
                                            loadOptions={loadOptions}
                                        />
                                    </div>
                                </div>

                                <div className='d-flex justify-content-end gap-2 mt-4'>
                                    <Button
                                        variant="secondary"
                                        onClick={handleCancel}
                                        disabled={isSubmitting}
                                        type="button"
                                    >
                                        Cancel
                                    </Button>
                                    <button
                                        type="submit"
                                        className="btn btn-light btn-active-primary"
                                        disabled={isSubmitting || state.loading}
                                    >
                                        {isSubmitting ? (id ? 'Updating...' : 'Creating...') : (id ? 'Update' : 'Create')}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    </div>
                </section>
            </main>
        </React.Fragment>
    );
};

export default Createexcludedpayer;
